import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import './MatchHistoryPage.css';

/* ─────────────────────────────────────────────────────────────
   MATCH HISTORY PAGE — shows any player's full match history.
   The :username URL param determines whose history is shown.
   Loads 50 matches at a time with a "Load more" button.
   ───────────────────────────────────────────────────────────── */

const PAGE_SIZE = 50;

/* Converts a UTC timestamp to a human-friendly relative string */
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)    return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4)   return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

/* Converts format code to a readable label */
function formatLabel(format) {
  const labels = { bo1: 'Single game', bo3: 'Best of 3', bo5: 'Best of 5', bo7: 'Best of 7' };
  return labels[format] || format;
}

function MatchHistoryPage() {
  const { username } = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();

  const [profileId,       setProfileId]       = useState(null);
  const [profileUsername, setProfileUsername] = useState('');
  const [isOwnHistory,    setIsOwnHistory]    = useState(false);
  const [matches,         setMatches]         = useState([]);
  const [offset,          setOffset]          = useState(0);
  const [hasMore,         setHasMore]         = useState(true);
  const [loading,         setLoading]         = useState(true);
  const [loadingMore,     setLoadingMore]     = useState(false);
  const [error,           setError]           = useState('');

  // ── Step 1: look up the profile for the username in the URL ──
  useEffect(() => {
    if (username) fetchProfile();
  }, [username]);

  async function fetchProfile() {
    setLoading(true);
    setError('');
    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('id, username')
      .eq('username', username)
      .single();

    if (fetchError || !data) {
      setError('Player not found.');
      setLoading(false);
      return;
    }

    setProfileId(data.id);
    setProfileUsername(data.username);
    setIsOwnHistory(data.id === user?.id);
  }

  // ── Step 2: fetch first batch once profileId is ready ────────
  useEffect(() => {
    if (profileId) loadMatches(0, true);
  }, [profileId]);

  // ── Fetch a batch of matches starting at `startOffset` ───────
  // isInitial=true replaces the list; false appends (load more).
  async function loadMatches(startOffset, isInitial = false) {
    isInitial ? setLoading(true) : setLoadingMore(true);
    setError('');

    const { data, error: fetchError } = await supabase
      .from('matches')
      .select(`
        id, format, winner_games_won, loser_games_won,
        winner_elo_change, loser_elo_change, played_at,
        status, reporter_id,
        winner:profiles!winner_id(id, username),
        loser:profiles!loser_id(id, username)
      `)
      .or(`winner_id.eq.${profileId},loser_id.eq.${profileId}`)
      .order('played_at', { ascending: false })
      .range(startOffset, startOffset + PAGE_SIZE - 1);

    isInitial ? setLoading(false) : setLoadingMore(false);

    if (fetchError) {
      setError('Could not load match history. Please try again.');
      return;
    }

    const batch = data || [];
    if (batch.length < PAGE_SIZE) setHasMore(false);

    setMatches(prev => isInitial ? batch : [...prev, ...batch]);
    setOffset(startOffset + PAGE_SIZE);
  }

  // ── Loading state ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="mhp-page">
        <div className="mhp-inner">
          <div className="mhp-loading">
            <span>★</span><span>★</span><span>★</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mhp-page">
      <div className="mhp-inner">

        {/* Back button — sits above the header, identical to profile page */}
        <button
          className="mhp-back"
          onClick={() => {
            // If the user navigated here directly (no history), fall back to
            // the profile page or dashboard rather than going nowhere.
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate(isOwnHistory ? '/dashboard' : `/profile/${profileUsername}`);
            }
          }}
        >
          ← Back
        </button>

        {/* Page title */}
        <div className="mhp-header">
          <h1 className="mhp-title">
            {isOwnHistory ? 'Match History' : `${profileUsername}'s Match History`}
          </h1>
          <p className="mhp-subtitle">
            {matches.length} match{matches.length !== 1 ? 'es' : ''} loaded
          </p>
        </div>

        {/* Error state */}
        {error && <p className="mhp-error">{error}</p>}

        {/* Empty state */}
        {!error && matches.length === 0 && (
          <div className="mhp-empty">
            <p className="mhp-empty-title">No matches yet</p>
            <p className="mhp-empty-sub">
              {isOwnHistory
                ? 'Head back to the dashboard to report your first match.'
                : `${profileUsername} hasn't played any matches yet.`}
            </p>
            <Link
              to={isOwnHistory ? '/dashboard' : `/profile/${profileUsername}`}
              className="mhp-empty-link"
            >
              {isOwnHistory ? 'Go to dashboard →' : `Back to ${profileUsername}'s profile →`}
            </Link>
          </div>
        )}

        {/* Match list — reuses mh-* classes from MatchHistory.css */}
        {matches.length > 0 && (
          <div className="mh-list">
            {matches.map(match => {
              const isWin       = match.winner?.id === profileId;
              const opponent    = isWin ? match.loser : match.winner;
              const myGames     = isWin ? match.winner_games_won : match.loser_games_won;
              const oppGames    = isWin ? match.loser_games_won  : match.winner_games_won;
              const eloChange   = isWin ? match.winner_elo_change : match.loser_elo_change;
              const isPending   = match.status === 'pending';
              const isConfirmed = match.status === 'confirmed' || !match.status;
              const isReporter  = match.reporter_id === user?.id;

              return (
                <div key={match.id} className={`mh-row ${isPending ? 'mh-row-pending' : isWin ? 'mh-row-win' : 'mh-row-loss'}`}>

                  {/* Result pill */}
                  <span className={`mh-result ${isPending ? 'mh-result-pending' : isWin ? 'mh-result-win' : 'mh-result-loss'}`}>
                    {isPending ? '…' : isWin ? 'W' : 'L'}
                  </span>

                  {/* Opponent + format */}
                  <div className="mh-opponent">
                    {opponent?.username ? (
                      <Link to={`/profile/${opponent.username}`} className="mh-opponent-link">
                        {opponent.username}
                      </Link>
                    ) : (
                      <span className="mh-opponent-deleted">Deleted User</span>
                    )}
                    <span className="mh-format">{formatLabel(match.format)}</span>
                  </div>

                  {/* Status badge — non-confirmed only; placeholder holds the cell for confirmed */}
                  {!isConfirmed ? (
                    <span className={`mh-status-badge mh-status-${match.status}`}>
                      {match.status === 'pending'   ? 'Pending'   :
                       match.status === 'denied'    ? 'Declined'  :
                       match.status === 'cancelled' ? 'Cancelled' : match.status}
                    </span>
                  ) : <span className="mh-col-placeholder" />}

                  {/* Score */}
                  <span className="mh-score">{myGames}–{oppGames}</span>

                  {/* ELO change — confirmed only; placeholder holds the cell for non-confirmed */}
                  {isConfirmed ? (
                    <span className={`mh-elo ${eloChange >= 0 ? 'mh-elo-gain' : 'mh-elo-loss'}`}>
                      {eloChange >= 0 ? `+${eloChange}` : eloChange}
                    </span>
                  ) : <span className="mh-col-placeholder" />}

                  {/* Date — or cancel button for reporter's pending matches */}
                  {isPending && isReporter ? (
                    <div className="mh-row-actions">
                      <button
                        className="mh-action-btn mh-action-cancel"
                        title="Cancel match request"
                        onClick={async () => {
                          const { error } = await supabase.rpc('cancel_match', { p_match_id: match.id });
                          if (error) { console.error('cancel_match error:', error); return; }
                          window.dispatchEvent(new CustomEvent('ranktt:matchResolved'));
                          loadMatches(0, true);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <span className="mh-time">{timeAgo(match.played_at)}</span>
                  )}

                </div>
              );
            })}
          </div>
        )}

        {/* Load more / all loaded footer */}
        {matches.length > 0 && (
          <div className="mhp-footer">
            {hasMore ? (
              <button
                className="mhp-load-more"
                onClick={() => loadMatches(offset)}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            ) : (
              <p className="mhp-all-loaded">All matches loaded</p>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default MatchHistoryPage;
